import { useEffect } from "react";
import {
  CHECK_INTERVAL_MS,
  DEFAULT_BUILD_REFRESH_MODE,
  FOREGROUND_CHECK_THROTTLE_MS,
  INITIAL_CHECK_DELAY_MS,
  LAST_CHECK_STORAGE_KEY,
  getBuildRefreshModeFromPolicy,
  attemptChunkLoadRecovery,
  attemptFirestoreClientRecovery,
  attemptRefreshForNewBuild,
  getCurrentMainAssetPath,
  getManifestBuildIdentifier,
  getManifestMainAssetPath,
  normalizeAssetPath,
  shouldAttemptChunkRecovery,
  shouldAttemptPageRecovery,
  shouldCheckForNewBuild,
  shouldRefreshForNewBuild,
  normalizeBuildRefreshMode,
  stripForcedRefreshParam,
} from "../services/buildRefresh";

const isLocalHost = (hostname = window.location.hostname) =>
  hostname === "localhost" ||
  hostname === "127.0.0.1" ||
  hostname === "[::1]" ||
  hostname.endsWith(".local");

const readStorage = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch (error) {
    return null;
  }
};

const getWindowStorage = (storageName) => {
  try {
    return window?.[storageName] || null;
  } catch (error) {
    return null;
  }
};

function BuildRefreshGate() {
  useEffect(() => {
    if (process.env.NODE_ENV !== "production" || isLocalHost()) {
      return undefined;
    }

    const cleanedPath = stripForcedRefreshParam(window.location.href);

    if (cleanedPath) {
      window.history.replaceState(window.history.state, "", cleanedPath);
    }

    let timeoutId = null;
    let intervalId = null;
    let isUnmounted = false;
    let isChecking = false;
    let lastForegroundCheckAt = 0;

    const scheduleCheck = (delayMs = 0, options = {}) => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }

      timeoutId = window.setTimeout(() => {
        timeoutId = null;
        void checkForNewBuild(options);
      }, delayMs);
    };

    const scheduleForegroundCheck = () => {
      if (document.visibilityState === "hidden") {
        return;
      }

      const now = Date.now();

      if (now - lastForegroundCheckAt < FOREGROUND_CHECK_THROTTLE_MS) {
        return;
      }

      lastForegroundCheckAt = now;
      scheduleCheck(0, { force: true });
    };

    const checkForNewBuild = async ({ force = false } = {}) => {
      if (
        isUnmounted ||
        isChecking ||
        document.visibilityState === "hidden"
      ) {
        return;
      }

      if (!force) {
        const lastCheckedAt = Number(
          readStorage(getWindowStorage("localStorage"), LAST_CHECK_STORAGE_KEY),
        );

        if (!shouldCheckForNewBuild(lastCheckedAt)) {
          return;
        }
      }

      isChecking = true;

      try {
        await attemptRefreshForNewBuild();
      } catch (error) {
        // Ignore transient network failures. The next foreground session can retry.
      } finally {
        isChecking = false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        scheduleForegroundCheck();
      }
    };

    const handlePageShow = () => {
      scheduleForegroundCheck();
    };

    scheduleCheck(INITIAL_CHECK_DELAY_MS, { force: true });
    intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        void checkForNewBuild();
      }
    }, CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      isUnmounted = true;

      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }

      if (intervalId !== null) {
        window.clearInterval(intervalId);
      }

      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  return null;
}

export default BuildRefreshGate;

export {
  getCurrentMainAssetPath,
  getManifestBuildIdentifier,
  getManifestMainAssetPath,
  normalizeAssetPath,
  attemptChunkLoadRecovery,
  attemptFirestoreClientRecovery,
  attemptRefreshForNewBuild,
  DEFAULT_BUILD_REFRESH_MODE,
  getBuildRefreshModeFromPolicy,
  normalizeBuildRefreshMode,
  shouldAttemptChunkRecovery,
  shouldAttemptPageRecovery,
  shouldCheckForNewBuild,
  shouldRefreshForNewBuild,
  stripForcedRefreshParam,
};
